import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '#src/__generated__/globalTypes.ts';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { Bold } from '#src/atoms/typography/Bold/index.tsx';
import { HeadlineM, LabelL } from '#src/atoms/typography/index.tsx';

import { DetailedScoreLine } from './DetailedScoreLine/index.tsx';
import type { CollectionGameHelper_cardCollection } from './__generated__/index.graphql.ts';
import books from './assets/books.webp';

const Illustration = styled.img`
  height: 80px;
`;
const WarningWrapper = styled(Horizontal)`
  align-items: flex-start;
  b {
    color: var(--c-yellow-400);
  }
`;
const WarningIconWrapper = styled.div`
  color: var(--c-yellow-400);
`;
type Props = {
  cardCollection: Nullable<CollectionGameHelper_cardCollection>;
};

export const CollectionGameHelper = ({ cardCollection }: Props) => {
  const { rarity, scoringMatrix } = cardCollection || {};
  const isCommonCollection = rarity === Rarity.common;

  return (
    <>
      <Vertical gap={2} center>
        <Illustration src={books} />
        <Vertical gap={0} className="text-center">
          <HeadlineM>
            <FormattedMessage
              id="collections.DetailsDialog.scoringMatrix"
              defaultMessage="Scoring Matrix"
            />
          </HeadlineM>
          <LabelL color="var(--c-nd-600)">
            <FormattedMessage
              id="collections.DetailsDialog.section1.description"
              defaultMessage="Each card has a collection score based on the following"
            />
          </LabelL>
        </Vertical>
      </Vertical>
      <Vertical gap={2}>
        {scoringMatrix?.map(value => {
          return (
            <DetailedScoreLine
              key={value.name}
              detailedScore={value}
              rarity={rarity}
            />
          );
        })}
      </Vertical>
      <WarningWrapper>
        <WarningIconWrapper>
          <FontAwesomeIcon icon={faCircleExclamation} />
        </WarningIconWrapper>
        <span>
          {isCommonCollection ? (
            <FormattedMessage
              id="collections.Warning.message2.common"
              defaultMessage="If a card is <b>dropped</b>, the points do not count in the collection score"
              values={{ b: Bold }}
            />
          ) : (
            <FormattedMessage
              id="collections.Warning.message2"
              defaultMessage="If a card is <b>listed on the market</b> the points do not count in the collection score"
              values={{ b: Bold }}
            />
          )}
        </span>
      </WarningWrapper>
    </>
  );
};

CollectionGameHelper.fragments = {
  cardCollection: gql`
    fragment CollectionGameHelper_cardCollection on CardCollection {
      slug
      rarity
      scoringMatrix {
        name
        pictureUrl
        label
        explanation
        value
      }
    }
  ` as TypedDocumentNode<CollectionGameHelper_cardCollection>,
};
